import {DateTime} from 'luxon';
import {z} from 'zod';
import {browserTimeZone, emptyToNull, zDateOnly, zNumberToString} from '@brunata-angular/util';
import {
    ExportColumnState,
    HeaderRowMode,
    UnitPlacement,
    headerRowModeSchema,
    unitPlacementSchema,
} from '../shared/models/export-columns.model';
import {
    outputFormatExtension,
    SavedExportOutputFormat,
    savedExportOutputFormats,
} from '../shared/models/output-format.model';
import {ExportFileFormat, exportFileFormatSchema} from '../shared/models/export-file-format.model';
import {deliveryOutcomes} from './delivery-log.model';
import {ReadingResolution, readingResolutionSchema} from '../shared/models/reading-shape.model';

export const maxSavedExportRecipients = 10;

// Matches savedexports.name, so an overlong name is caught before the backend answers 400.
export const maxSavedExportNameLength = 160;

export const savedExportRecurrences = [
    'DAILY_8AM',
    'WEEKLY_EVERY_MONDAY_8AM',
    'BIMONTHLY_EVERY_SECOND_MONDAY_8AM',
    'MONTHLY_FIRST_8AM',
] as const;

export const savedExportRecurrenceSchema = z.enum(savedExportRecurrences);

export type SavedExportRecurrence = z.infer<typeof savedExportRecurrenceSchema>;

export const recurrenceLabelKeys: Record<SavedExportRecurrence, string> = {
    DAILY_8AM: 'savedexport_recurrence_daily_8am',
    WEEKLY_EVERY_MONDAY_8AM: 'savedexport_recurrence_weekly_monday_8am',
    BIMONTHLY_EVERY_SECOND_MONDAY_8AM: 'savedexport_recurrence_bimonthly_second_monday_8am',
    MONTHLY_FIRST_8AM: 'savedexport_recurrence_monthly_first_8am',
};

const onceScheduleLabelKey = 'savedexport_recurrence_once';
const customScheduleLabelKey = 'savedexport_recurrence_custom';

export const savedExportScheduleLabelKeys = [
    ...savedExportRecurrences.map(recurrence => recurrenceLabelKeys[recurrence]),
    onceScheduleLabelKey,
    customScheduleLabelKey,
];

export const savedExportDeliveryChannels = ['EMAIL', 'SFTP'] as const;

export const savedExportDeliveryChannelSchema = z.enum(savedExportDeliveryChannels);

export type SavedExportDeliveryChannel = z.infer<typeof savedExportDeliveryChannelSchema>;

export const deliveryChannelLabelKeys: Record<SavedExportDeliveryChannel, string> = {
    EMAIL: 'savedexport_email_delivery_heading',
    SFTP: 'savedexport_sftp_delivery_heading',
};

export const savedExportScheduleUnits = ['HOURS', 'DAYS', 'WEEKS', 'MONTHS'] as const;

export const savedExportScheduleUnitSchema = z.enum(savedExportScheduleUnits);

export type SavedExportScheduleUnit = z.infer<typeof savedExportScheduleUnitSchema>;

export const scheduleUnitLabelKeys: Record<SavedExportScheduleUnit, string> = {
    HOURS: 'savedexport_schedule_unit_hours',
    DAYS: 'savedexport_schedule_unit_days',
    WEEKS: 'savedexport_schedule_unit_weeks',
    MONTHS: 'savedexport_schedule_unit_months',
};

export const savedExportSchedulePeriods = ['DAYS_BACK', 'ENDED_MONTHS'] as const;

export const savedExportSchedulePeriodSchema = z.enum(savedExportSchedulePeriods);

export type SavedExportSchedulePeriod = z.infer<typeof savedExportSchedulePeriodSchema>;

export const savedExportWeekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

export const savedExportWeekdaySchema = z.enum(savedExportWeekdays);

export type SavedExportWeekday = z.infer<typeof savedExportWeekdaySchema>;

export const weekdayLabelKeys: Record<SavedExportWeekday, string> = {
    MON: 'savedexport_weekday_mon',
    TUE: 'savedexport_weekday_tue',
    WED: 'savedexport_weekday_wed',
    THU: 'savedexport_weekday_thu',
    FRI: 'savedexport_weekday_fri',
    SAT: 'savedexport_weekday_sat',
    SUN: 'savedexport_weekday_sun',
};

// Matches savedexports.sftpsubfolder and .filenamepattern.
export const maxSavedExportPathLength = 255;

export const minScheduleInterval = 1;

export const maxScheduleInterval = 365;

// Capped at 28 so every month has the day the user picked.
export const maxScheduleDayOfMonth = 28;

export const savedExportTypes = ['QUICK', 'CUSTOMIZED'] as const;

export type SavedExportType = (typeof savedExportTypes)[number];

export const savedExportColumnSchema = z.object({
    columnId: z.string(),
    included: z.boolean(),
    header: z.string().nullable(),
});

export type SavedExportColumn = z.infer<typeof savedExportColumnSchema>;

export const savedExportChannelSchema = z.object({
    meterSuperTypeNo: zNumberToString,
    modelConfigurationId: zNumberToString.nullable().default(null),
    obisCode: z.string().nullable().default(null),
    channelKey: z.string().nullable().default(null),
});

export type SavedExportChannel = z.infer<typeof savedExportChannelSchema>;

export type SavedExportChannelRequest =
    | {meterSuperTypeNo: number; modelConfigurationId: number; obisCode: string}
    | {meterSuperTypeNo: number; channelKey: string};

export function toSavedExportChannelRequest(
    channel: SavedExportChannel,
): SavedExportChannelRequest[] {
    const meterSuperTypeNo = Number(channel.meterSuperTypeNo);
    if (channel.modelConfigurationId !== null && channel.obisCode !== null) {
        return [
            {
                meterSuperTypeNo,
                modelConfigurationId: Number(channel.modelConfigurationId),
                obisCode: channel.obisCode,
            },
        ];
    }
    return channel.channelKey === null ? [] : [{meterSuperTypeNo, channelKey: channel.channelKey}];
}

export const savedExportSchema = z
    .object({
        savedExportId: zNumberToString,
        name: z.string(),
        recurrence: savedExportRecurrenceSchema.nullable(),
        startDate: zDateOnly.nullable(),
        recipients: z.array(z.string()),
        outputFormat: z.enum(savedExportOutputFormats),
        headerRow: headerRowModeSchema.default('EVERY_EXPORT'),
        unitPlacement: unitPlacementSchema.default('OWN_COLUMN'),
        columns: z.array(savedExportColumnSchema).default([]),
        timeZone: z.string().nullable().default(null),
        includeDismountedMeters: z.boolean().default(false),
        exportType: z.enum(savedExportTypes).default('QUICK'),
        readingResolution: readingResolutionSchema.nullable().default(null),
        daysBackInTime: z.number().nullable().default(null),
        superTypes: z.array(zNumberToString).default([]),
        channelLessSuperTypes: z.array(zNumberToString).default([]),
        channels: z.array(savedExportChannelSchema).default([]),
        systemIds: z.array(zNumberToString).default([]),
        autoIncludeFutureSystems: z.boolean().default(false),
        deliveryChannel: savedExportDeliveryChannelSchema.catch('EMAIL'),
        sftpConnectionId: zNumberToString.nullable().default(null),
        sftpSubfolder: z.string().nullable().default(null),
        fileNamePattern: z.string().nullable().default(null),
        scheduleUnit: savedExportScheduleUnitSchema.nullable().catch(null),
        scheduleInterval: z.number().nullable().default(null),
        scheduleWeekdays: z.array(savedExportWeekdaySchema).catch([]),
        scheduleDayOfMonth: z.number().nullable().default(null),
        schedulePeriod: savedExportSchedulePeriodSchema.nullable().catch(null),
        lastDeliveryDate: zDateOnly.nullable().default(null),
        lastDeliveryOutcome: z.enum(deliveryOutcomes).nullable().catch(null),
        deliveryRequested: z.boolean().optionalUntilDeployed('AGI-20036'),
        ...exportFileFormatSchema.shape,
    })
    .transform(savedExport => ({
        ...savedExport,
        scheduleLabelKey:
            savedExport.scheduleUnit !== null
                ? customScheduleLabelKey
                : savedExport.recurrence === null
                  ? onceScheduleLabelKey
                  : recurrenceLabelKeys[savedExport.recurrence],
        sftpConnectionMissing:
            savedExport.deliveryChannel === 'SFTP' && savedExport.sftpConnectionId === null,
    }));

export type SavedExport = z.infer<typeof savedExportSchema>;

export const savedExportPageSchema = z.object({
    totalCount: z.number(),
    savedExports: z.array(savedExportSchema),
});

export type SavedExportPage = z.infer<typeof savedExportPageSchema>;

// Edit (AGI-18213) reuses this same shape: it overwrites the saved format in place via PATCH with
// the same fields the create flow persists.
export type SavedExportCreateRequest = {
    outputFormat: SavedExportOutputFormat;
    name: string;
    exportType: SavedExportType;
    recurrence: SavedExportRecurrence | null;
    startDate: string | null;
    recipients: string[];
    headerRow?: HeaderRowMode;
    unitPlacement?: UnitPlacement;
    columns?: SavedExportColumn[];
    timeZone: string;
    includeDismountedMeters: boolean;
    readingResolution: ReadingResolution | null;
    daysBackInTime: number | null;
    superTypes?: number[];
    channelLessSuperTypes?: number[];
    channels?: SavedExportChannelRequest[];
    systemIds?: number[];
    autoIncludeFutureSystems?: boolean;
    deliveryChannel: SavedExportDeliveryChannel;
    sftpConnectionId: number | null;
    sftpSubfolder: string | null;
    fileNamePattern: string | null;
    scheduleUnit: SavedExportScheduleUnit | null;
    scheduleInterval: number | null;
    scheduleWeekdays: SavedExportWeekday[];
    scheduleDayOfMonth: number | null;
    schedulePeriod?: SavedExportSchedulePeriod;
} & ExportFileFormat;

export type SavedExportColumnConfig = Required<
    Pick<SavedExportCreateRequest, 'headerRow' | 'unitPlacement' | 'columns'>
>;

export function toSavedExportColumnConfig(
    headerRow: HeaderRowMode,
    unitPlacement: UnitPlacement,
    columns: ReadonlyArray<ExportColumnState>,
): SavedExportColumnConfig {
    return {
        headerRow,
        unitPlacement,
        columns: columns.map(column => ({
            columnId: column.id,
            included: column.included,
            header: column.header,
        })),
    };
}

export type SavedExportTestRequest = SavedExportCreateRequest;

export const savedExportUsageSchema = z.enum(['on-demand', 'schedule', 'customized']);

export type SavedExportUsage = z.infer<typeof savedExportUsageSchema>;

export function savedExportFileName(
    name: string,
    readingDate: DateTime,
    outputFormat: SavedExportOutputFormat,
): string {
    const baseName = `${name.trim()}-${readingDate.toFormat('yyyy-MM-dd')}`;
    const extension = outputFormatExtension(outputFormat);
    return extension === null ? baseName : `${baseName}.${extension}`;
}

export const sftpDeliveryUniqueSuffixPlaceholder = 'xxxxxxxx';

const unnamedSftpDeliveryStem = 'export';

function toFileNameStem(name: string): string {
    return name
        .replace(/[/\\:*?"<>|#]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\.+$/, '');
}

export function sftpDeliveryFileName(
    pattern: string | null,
    exportName: string,
    readingDate: DateTime,
    outputFormat: SavedExportOutputFormat,
    deliveredAt: DateTime,
): string {
    const extension = outputFormatExtension(outputFormat);
    const dottedExtension = extension === null ? '' : `.${extension}`;
    const timestamp = deliveredAt.toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
    const resolved = (pattern ?? '')
        .replaceAll('{date}', readingDate.toFormat('yyyyMMdd'))
        .replaceAll('{timestamp}', timestamp)
        .replaceAll('{format}', extension ?? '');
    const named = toFileNameStem(resolved) || toFileNameStem(exportName) || unnamedSftpDeliveryStem;
    const stem = (
        dottedExtension !== '' && named.toLowerCase().endsWith(dottedExtension)
            ? named.slice(0, -dottedExtension.length)
            : named
    ).replace(/[ .]+$/, '');
    return `${stem}_${timestamp}_${sftpDeliveryUniqueSuffixPlaceholder}${dottedExtension}`;
}

export function hasMeterScope(savedExport: SavedExport): boolean {
    return (
        savedExport.superTypes.length > 0 ||
        savedExport.channelLessSuperTypes.length > 0 ||
        savedExport.channels.length > 0 ||
        savedExport.systemIds.length > 0 ||
        savedExport.autoIncludeFutureSystems
    );
}

export function savedExportUsage(savedExport: SavedExport): SavedExportUsage {
    if (savedExport.exportType === 'CUSTOMIZED') {
        return 'customized';
    }
    return savedExport.recurrence === null ? 'on-demand' : 'schedule';
}

export function toSavedExportCreateRequest(savedExport: SavedExport): SavedExportCreateRequest {
    return {
        outputFormat: savedExport.outputFormat,
        name: savedExport.name,
        exportType: savedExport.exportType,
        recurrence: savedExport.recurrence,
        startDate: savedExport.startDate?.toISODate() ?? null,
        recipients: savedExport.recipients,
        headerRow: savedExport.headerRow,
        unitPlacement: savedExport.unitPlacement,
        columns: savedExport.columns,
        timeZone: savedExport.timeZone ?? browserTimeZone(),
        includeDismountedMeters: savedExport.includeDismountedMeters,
        readingResolution: savedExport.readingResolution,
        daysBackInTime: savedExport.daysBackInTime,
        deliveryChannel: savedExport.deliveryChannel,
        sftpConnectionId:
            savedExport.sftpConnectionId === null ? null : Number(savedExport.sftpConnectionId),
        sftpSubfolder: savedExport.sftpSubfolder,
        fileNamePattern: savedExport.fileNamePattern,
        scheduleUnit: savedExport.scheduleUnit,
        scheduleInterval: savedExport.scheduleInterval,
        scheduleWeekdays: savedExport.scheduleWeekdays,
        scheduleDayOfMonth: savedExport.scheduleDayOfMonth,
        ...(savedExport.schedulePeriod === null
            ? {}
            : {schedulePeriod: savedExport.schedulePeriod}),
        ...(hasMeterScope(savedExport)
            ? {
                  superTypes: savedExport.superTypes.map(Number),
                  channelLessSuperTypes: savedExport.channelLessSuperTypes.map(Number),
                  channels: savedExport.channels.flatMap(toSavedExportChannelRequest),
                  systemIds: savedExport.systemIds.map(Number),
                  autoIncludeFutureSystems: savedExport.autoIncludeFutureSystems,
              }
            : {}),
        ...savedExportFileFormat(savedExport),
    };
}

export function savedExportFileFormat(savedExport: SavedExport): ExportFileFormat {
    return {
        encoding: savedExport.encoding,
        fieldSeparator: savedExport.fieldSeparator,
        textDelimiter: savedExport.textDelimiter,
        decimalSeparator: savedExport.decimalSeparator,
        decimalPrecision: savedExport.decimalPrecision,
        dateFormat: savedExport.dateFormat,
        timestampFormat: savedExport.timestampFormat,
    };
}

export type ExportScheduleValue = {
    recurrence: SavedExportRecurrence;
    startDate: DateTime | null;
    recipients: string[];
    deliveryChannel: SavedExportDeliveryChannel;
    sftpConnectionId: string | null;
    sftpSubfolder: string | null;
    fileNamePattern: string | null;
    customSchedule: boolean;
    scheduleUnit: SavedExportScheduleUnit;
    scheduleInterval: number;
    scheduleWeekdays: SavedExportWeekday[];
    scheduleDayOfMonth: number;
    schedulePeriod: SavedExportSchedulePeriod;
};

export const defaultSavedExportRecurrence: SavedExportRecurrence = 'WEEKLY_EVERY_MONDAY_8AM';

export const defaultScheduleUnit: SavedExportScheduleUnit = 'DAYS';

export const defaultExportScheduleValue: ExportScheduleValue = {
    recurrence: defaultSavedExportRecurrence,
    startDate: null,
    recipients: [],
    deliveryChannel: 'EMAIL',
    sftpConnectionId: null,
    sftpSubfolder: null,
    fileNamePattern: null,
    customSchedule: false,
    scheduleUnit: defaultScheduleUnit,
    scheduleInterval: minScheduleInterval,
    scheduleWeekdays: [],
    scheduleDayOfMonth: 1,
    schedulePeriod: 'DAYS_BACK',
};

/** A stored saved export as the shared schedule form group holds it. */
export function savedExportScheduleValue(savedExport: SavedExport): ExportScheduleValue {
    return {
        recurrence: savedExport.recurrence ?? defaultSavedExportRecurrence,
        startDate: savedExport.startDate,
        recipients: [...savedExport.recipients],
        deliveryChannel: savedExport.deliveryChannel,
        sftpConnectionId: savedExport.sftpConnectionId,
        sftpSubfolder: savedExport.sftpSubfolder,
        fileNamePattern: savedExport.fileNamePattern,
        customSchedule: savedExport.scheduleUnit !== null,
        scheduleUnit: savedExport.scheduleUnit ?? defaultScheduleUnit,
        scheduleInterval: savedExport.scheduleInterval ?? minScheduleInterval,
        scheduleWeekdays: [...savedExport.scheduleWeekdays],
        scheduleDayOfMonth: savedExport.scheduleDayOfMonth ?? 1,
        schedulePeriod: savedExport.schedulePeriod ?? 'DAYS_BACK',
    };
}

export type SavedExportScheduleFields = Pick<
    SavedExportCreateRequest,
    | 'recurrence'
    | 'startDate'
    | 'recipients'
    | 'deliveryChannel'
    | 'sftpConnectionId'
    | 'sftpSubfolder'
    | 'fileNamePattern'
    | 'scheduleUnit'
    | 'scheduleInterval'
    | 'scheduleWeekdays'
    | 'scheduleDayOfMonth'
    | 'schedulePeriod'
>;

export function savedExportScheduleFields(
    scheduled: boolean,
    schedule: ExportScheduleValue,
): SavedExportScheduleFields {
    if (!scheduled) {
        return {
            recurrence: null,
            startDate: null,
            recipients: [],
            deliveryChannel: 'EMAIL',
            sftpConnectionId: null,
            sftpSubfolder: null,
            fileNamePattern: null,
            scheduleUnit: null,
            scheduleInterval: null,
            scheduleWeekdays: [],
            scheduleDayOfMonth: null,
        };
    }
    const overSftp = schedule.deliveryChannel === 'SFTP';
    const unit = schedule.customSchedule ? schedule.scheduleUnit : null;
    const endedMonths = unit === 'MONTHS' && schedule.schedulePeriod === 'ENDED_MONTHS';
    return {
        recurrence: schedule.recurrence,
        startDate: schedule.startDate?.toISODate() ?? null,
        recipients: overSftp ? [] : schedule.recipients,
        deliveryChannel: schedule.deliveryChannel,
        sftpConnectionId:
            overSftp && schedule.sftpConnectionId !== null
                ? Number(schedule.sftpConnectionId)
                : null,
        sftpSubfolder: overSftp ? emptyToNull(schedule.sftpSubfolder) : null,
        fileNamePattern: overSftp ? emptyToNull(schedule.fileNamePattern) : null,
        scheduleUnit: unit,
        scheduleInterval: unit === null ? null : schedule.scheduleInterval,
        scheduleWeekdays: unit === 'WEEKS' ? schedule.scheduleWeekdays : [],
        scheduleDayOfMonth: endedMonths
            ? 1
            : unit === 'MONTHS'
              ? schedule.scheduleDayOfMonth
              : null,
        ...(endedMonths ? {schedulePeriod: 'ENDED_MONTHS'} : {}),
    };
}
